// Thin wrapper over the `idb` library, keeping a stable generic CRUD surface for feature modules.
// Schema is driven by indexedDb.config.js so new stores can be added without touching this file.

import { openDB } from "idb";
import { DB_NAME, DB_VERSION, STORES } from "./indexedDb.config";

let dbPromise = null;

export function isIndexedDbSupported() {
    return typeof indexedDB !== "undefined";
}

function getDB() {
    if (dbPromise) return dbPromise;

    if (!isIndexedDbSupported()) {
        return Promise.reject(new Error("IndexedDB is not supported in this environment"));
    }

    dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, tx) {
            STORES.forEach((store) => {
                const objectStore = db.objectStoreNames.contains(store.name)
                    ? tx.objectStore(store.name)
                    : db.createObjectStore(store.name, store.options || {});

                (store.indexes || []).forEach((index) => {
                    if (!objectStore.indexNames.contains(index.name)) {
                        objectStore.createIndex(index.name, index.keyPath, index.options || {});
                    }
                });
            });
        },
        blocked() {
            console.warn("IndexedDB upgrade blocked — close other tabs running this app.");
        },
    });

    // Allow a later retry if the first open attempt failed.
    dbPromise.catch(() => { dbPromise = null; });

    return dbPromise;
}

// Generic CRUD surface shared by every feature-specific store module.
export const idb = {
    get: async (storeName, key) => (await getDB()).get(storeName, key),
    getAll: async (storeName, query, count) => (await getDB()).getAll(storeName, query, count),
    getAllKeys: async (storeName) => (await getDB()).getAllKeys(storeName),
    getAllFromIndex: async (storeName, indexName, query) =>
        (await getDB()).getAllFromIndex(storeName, indexName, query),
    count: async (storeName) => (await getDB()).count(storeName),
    put: async (storeName, value, key) => (await getDB()).put(storeName, value, key),
    delete: async (storeName, key) => (await getDB()).delete(storeName, key),
    clear: async (storeName) => (await getDB()).clear(storeName),
    bulkPut: async (storeName, values) => {
        const db = await getDB();
        const tx = db.transaction(storeName, "readwrite");
        await Promise.all([...(values || []).map((v) => tx.store.put(v)), tx.done]);
        return true;
    },
};
